import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Field, FieldLabel } from '@/components/ui/field'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchable-select'
import { type PolicyChangeInput, PolicyChangeKind, RemovalReason } from '@/models/policyChange'
import { toIsoDate } from './policyChangeCopy'
import { RemovalFields } from './RemovalFields'

/** A unit on the policies the change is about; value is "<type>:<id>" so trucks and trailers share a list. */
export interface UnitOption extends SearchableSelectOption {
  type: 'truck' | 'trailer' | 'driver'
}

interface PickUnitFormProps {
  kind: PolicyChangeKind
  units: UnitOption[]
  onAdd: (change: PolicyChangeInput) => Promise<boolean>
}

/** Take a truck, trailer or driver off the policies. */
export function PickUnitForm({ kind, units, onAdd }: PickUnitFormProps) {
  const vehicle = kind !== PolicyChangeKind.REMOVE_DRIVER
  const [picked, setPicked] = useState<string>()
  const [reason, setReason] = useState<RemovalReason>()
  const [termLetter, setTermLetter] = useState<Date | undefined>()
  const [lastLoad, setLastLoad] = useState<Date | undefined>()
  const [adding, setAdding] = useState(false)

  async function add() {
    const unit = units.find((option) => option.value === picked)
    if (!unit) return
    const id = unit.value.split(':')[1]
    setAdding(true)
    const added = await onAdd({
      kind,
      [`${unit.type}Id`]: id,
      removalReason: reason ?? '',
      termLetterDate: toIsoDate(termLetter),
      lastLoadDate: toIsoDate(lastLoad),
    }).finally(() => setAdding(false))
    if (!added) return
    setPicked(undefined)
    setReason(undefined)
    setTermLetter(undefined)
    setLastLoad(undefined)
  }

  return (
    <div className="flex flex-col gap-4">
      <Field>
        <FieldLabel htmlFor={`${kind}-unit`}>{vehicle ? 'Which vehicle?' : 'Which driver?'}</FieldLabel>
        <SearchableSelect
          id={`${kind}-unit`}
          options={units}
          value={picked}
          onChange={setPicked}
          placeholder={vehicle ? 'Pick a vehicle on your policies' : 'Pick a driver on your policies'}
          searchPlaceholder={vehicle ? 'Search unit number, VIN or plate' : 'Search name or license'}
          emptyMessage="None match that"
        />
      </Field>
      <RemovalFields
        kind={kind}
        reason={reason}
        termLetter={termLetter}
        lastLoad={lastLoad}
        onReason={setReason}
        onTermLetter={setTermLetter}
        onLastLoad={setLastLoad}
      />
      <div>
        <Button
          type="button"
          variant="outline"
          disabled={!picked}
          onClick={add}
          loading={adding ? 'Adding…' : undefined}
        >
          Add removal
        </Button>
      </div>
    </div>
  )
}
